import { GetXcoreStaticContext, useContentSingletons, usePageContent, useXcoreUtils } from "@robe/client/xcore-ui";
import { fetchRelationsForSingleContent, Localized, RichtextValue } from "@robe/core";
import { LayoutNoHead } from "components/Layout";
import RobeHead from "components/RobeHead";
import ProductDetailTemplate from "components/Product/ProductDetail";
import { isLocalizedObjectString } from "utils/migration";
import { useTagManager } from "hooks/useTagManager";
import { getRobePaths, getRobeProps, RobePage } from "xcore";
import { Accessory, Inquiry, News, Product, ProductDetailPage, Products } from "xcore/types";
import { ProductDetailSubheader } from "components/Product/ProductDetail/ProductDetailSubheader";
import { useRouter } from "next/router";
import { useProductGoogleStructuredData } from "utils/googleStructuredData/useProductGoogleStructuredData";
import { NewsCardNewsValues, newsCardNewsValuesFields, productCardProductFields } from "design-system/robe";
import { shortVideoFields, videoFields } from "xcore/relationsFields";

export const getXcoreProps = async ({ core, id, locale }: GetXcoreStaticContext) => {
  const reverseRelations = await core.content.getReverseRelations(id!);
  const relatedNewsIDs = reverseRelations.filter(({ contentType }) => contentType === "news").map(({ contentId }) => contentId);

  const pageContent = await core.content
    .singleById<Product | Accessory>(id!, {
      includeValues: true,
      includeCategories: true,
      // todo xcc after localized images migrated remove workaround
      ...(locale === "ru" && {
        locales: ["ru", "en"],
      }),
    })
    .then(
      fetchRelationsForSingleContent({
        core,
        relationSourceFields: [
          { field: "type", ofListObjectField: "specification" },
          { field: "wheels", ofListObjectField: "wheels" },
          { field: "wheelSet", ofListObjectField: "wheels" },
          { field: "videos" },
          { field: "keyFeatures" },
          { field: "accessories" },
          { field: "innovations" },
          { field: "type", ofListObjectField: "paramsOrFeatures" },
        ],
        options: {
          fields: [
            ...new Set([...videoFields, ...shortVideoFields, ...productCardProductFields, "image", "large", "icon", "shortcut", "intro"]),
          ],
          // todo xcc after localized images migrated remove workaround
          ...(locale === "ru" && {
            locales: ["ru", "en"],
          }),
        },
      })
    );

  const multipleContents = [];

  if (relatedNewsIDs.length !== 0) {
    multipleContents.push(
      core.content.listByIds<News, NewsCardNewsValues>(relatedNewsIDs, {
        fields: newsCardNewsValuesFields,
        sort: { fieldName: "publishedAt", orderBy: "Descending" },
        perPage: 4,
        // todo xcc after localized images migrated remove workaround
        ...(locale === "ru" && {
          locales: ["ru", "en"],
        }),
      })
    );
  }

  return {
    pageContent,
    contentSingletons: [
      core.content.singletonByType<ProductDetailPage>("productDetailPage", {
        fields: [
          "anchorAccessories",
          "accessories",
          "anchorGobos",
          "anchorDownloads",
          "displaySuppFiles",
          "anchorAbout",
          "downloads",
          "downloadIntro",
          "dataSheetMenu",
          "downloadOriginalImageButtonText",
          "downloadJPGButtonText",
          "aboutMenu",
          "innovation",
          "anchorKeyFeatures",
          "keyFeaturesTitle",
          "anchorRelatedNews",
          "relatedNews",
          "anchorProductPictures",
          "pictures",
          "anchorTechnicalSpecs",
          "technicalSpec",
          "expandAll",
          "downloadPdf",
          "collapseAll",
          "anchorVideo",
          "productVid",
          "gobosColors",
          "inquiryBtn",
          "contactBtn",
        ],
      }),
      core.content.singletonByType<Inquiry>("inquiry", { includeValues: true }),
      core.content.singletonByType<Products>("products", { fields: ["iconLegends"] }),
    ],
    multipleContents,
    reverseRelations: [reverseRelations],
  };
};

export const getStaticProps = getRobeProps(getXcoreProps);

export const getStaticPaths = getRobePaths("product");

const ProductDetail: RobePage = () => {
  const { stringify, localize, richtextToString, image } = useXcoreUtils();
  const { values, subCategories } = usePageContent<Product>();
  const { pathname } = useRouter();
  const isAccessoryPage = pathname.includes("/product/accessory");

  const [, , products] = useContentSingletons<[any, any, Products]>();

  // fetch icon names dictionary and get corresponding icon name based on product icon name value
  const productIconName = products.values?.iconLegends?.find((icon) => icon.type === values.productMenuIcon)?.title;

  useTagManager("product-detail");

  const metaTitle = stringify(values?.metaTitle);
  const description = values?.metaDescription
    ? isLocalizedObjectString(values?.metaDescription)
      ? stringify(values.metaDescription as unknown as Localized<string>)
      : richtextToString(values.metaDescription as unknown as Localized<RichtextValue>)
    : richtextToString(values.description);

  const title = [
    stringify(values.name),
    subCategories?.["product-type"]
      ? `${subCategories["product-type"].map((pt) => stringify(pt.name)).join(" ")} ${values.ipCertifiedIcon ? "IP65" : ""} ${stringify(productIconName) ?? ""}`
      : "",
    subCategories?.["beam-type"] ? `${subCategories["beam-type"].map((bt) => stringify(bt.name)).join(" ")}` : "",
  ];

  const productImage = localize(values.images)?.[0];
  const keyFeatures = localize(values.keyFeatures);

  const productGoogleStructuredData = useProductGoogleStructuredData(isAccessoryPage, title, keyFeatures);

  return (
    <>
      <RobeHead
        googleStructuredData={productGoogleStructuredData}
        title={title}
        {...(metaTitle && metaTitle !== "" ? { metaTitle } : { metaTitle: title })}
        description={description}
        {...(productImage && { img: image(productImage, { width: 1200, height: 630 }) })}
      />
      <ProductDetailTemplate />
    </>
  );
};

ProductDetail.Layout = LayoutNoHead;
ProductDetail.Subheader = ProductDetailSubheader;

export default ProductDetail;
